import React, { useState, useRef, useEffect } from 'react';
import { Html5QrcodeScanner } from 'html5-qrcode';

const QRScannerModal = ({ isOpen, onClose, onScanSuccess }) => {
  const [error, setError] = useState('');
  const html5QrCodeScanner = useRef(null);

  const parseQRData = (qrText) => {
    try {
      let data = {};

      // Case 1: JSON format (Recommended)
      if (qrText.startsWith('{')) {
        data = JSON.parse(qrText);
      } 
      // Case 2: Pipe separated → CODE|NAME|SHOPNAME
      else if (qrText.includes('|')) {
        const [code, name, shop] = qrText.split('|').map(s => s.trim());
        data = { code, name, shopName: shop };
      } 
      // Case 3: Plain text (only code)
      else {
        data = { code: qrText.trim() };
      }

      const result = {
        shopkeeperCode: data.code || data.shopkeeperCode || '',
        shopkeeperName: data.name || data.shopkeeperName || '',
        shopName: data.shopName || data.shop || '',
        latitude: data.latitude || data.lat || null,
        longitude: data.longitude || data.lng || null,
        scannedAt: new Date().toISOString(),
      };

      // Validate required fields
      if (!result.shopkeeperCode) {
        throw new Error("Missing merchant code");
      }

      return result;
    } catch (err) {
      return null;
    }
  };

  const startScanning = () => {
    setError('');

    const scanner = new Html5QrcodeScanner(
      "qr-reader",
      {
        fps: 15,
        qrbox: { width: 280, height: 280 },
        aspectRatio: 1.0,
        disableFlip: false,
        rememberLastUsedCamera: true,
        showTorchButton: true,
        showZoomSlider: true,
      },
      false
    );

    html5QrCodeScanner.current = scanner;

    scanner.render(
      (decodedText) => {
        const parsedData = parseQRData(decodedText);

        if (!parsedData) {
          setError("Invalid QR Code! Missing required merchant details.");
          return;
        }

        stopScanner();

        // Capture the scanning device's live location (not from the QR itself)
        if (!navigator.geolocation) {
          onScanSuccess({ ...parsedData, scanLatitude: null, scanLongitude: null });
          return;
        }

        navigator.geolocation.getCurrentPosition(
          (pos) => {
            onScanSuccess({
              ...parsedData,
              scanLatitude: pos.coords.latitude,
              scanLongitude: pos.coords.longitude,
            });
          },
          () => {
            onScanSuccess({ ...parsedData, scanLatitude: null, scanLongitude: null });
          },
          { enableHighAccuracy: true, timeout: 8000 }
        );
      },
      () => {} // Ignore scan failures (normal)
    );
  };

  const stopScanner = () => {
    if (html5QrCodeScanner.current) {
      html5QrCodeScanner.current.clear().catch(() => {});
      html5QrCodeScanner.current = null;
    }
  };

  const handleClose = () => {
    stopScanner();
    setError('');
    onClose();
  };

  // Auto start scanning when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(startScanning, 300);
    }
    return () => stopScanner();
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-2xl w-full max-w-md overflow-hidden">
        <div className="p-4 border-b border-gray-700 flex justify-between items-center">
          <h3 className="text-white font-semibold text-lg">Scan Merchant QR Code</h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-white text-2xl leading-none"
          >
            ✕
          </button>
        </div>

        <div className="p-6">
          <div id="qr-reader" className="rounded-xl overflow-hidden bg-black" />
        </div>

        {error && (
          <div className="px-6 pb-4">
            <p className="text-red-400 text-sm text-center">{error}</p>
          </div>
        )}

        <div className="p-4 border-t border-gray-700 text-center text-xs text-gray-500">
          Point camera at QR code sticker<br />
          Location & timestamp will be captured automatically
        </div>
      </div>
    </div>
  );
};

export default QRScannerModal;